import React, {useState, useCallback, useEffect} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import {makeStyles} from '@components/@theme';
import cn from 'classnames';

// TODO probably belongs somewhere else
export interface AutocompleteResults<T> {
  total: number;
  results: T[];
}

interface Props<T> {
  // Should return options based on the input's search string
  fullSource: (search: string) => Promise<T[]>;
  // Should return commonly used options to render when no input has been provided.
  commonSource: () => Promise<AutocompleteResults<T>>;

  // OnChange hook to set value in parent
  setValue: (val: T) => void;
  // current value of the dropdown
  value?: T;

  // Placeholder text that appears when nothing is selected
  placeholder?: string;
  // Width of the element
  width?: number;

  // Determines whether an option should be greyed out.
  isOptionDisabled?: (opt: T) => boolean;
  // Function returning the string to use for each options label in the dropdown
  // Defaults to using the `label` field
  getLabel?: (opt: T) => string;
  // Function returning a field to group elements by
  getGroup?: (opt: T) => string;
  // Function which returns text shown above common source's truncated results.
  // If the function returns null then the banner will not be rendered.
  previewLabel?: (resultSize: number, total: number) => string | null;
  className?: string;
  error?: boolean;
}

enum Source {
  COMMON = 'COMMON',
  ASYNC = 'ASYNC',
}

export default function SingleAutocomplete<T>({
  fullSource,
  commonSource,
  value,
  setValue,
  placeholder,

  getLabel = (opt: any) => opt.label,
  isOptionDisabled,
  getGroup,
  className,
  width,
  previewLabel,
  error,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [commonOptions, setCommonOptions] = useState<T[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sourceType, setSourceType] = useState<Source>(Source.COMMON);

  useEffect(() => {
    commonSource().then(({total, results}) => {
      setTotalResults(total);
      setCommonOptions(results);
    });
  }, [commonSource, setCommonOptions, setTotalResults]);

  const debouncedSource = debounce(str => {
    if (!str || str === '' || str.length < 2) {
      setOpen(true);
      setSourceType(Source.COMMON);
    } else {
      setSourceType(Source.ASYNC);
      setLoading(true);
      fullSource(str).then(opts => {
        setOpen(true);
        setOptions(opts);
        setLoading(false);
      });
    }
  }, 300);

  const onChange = useCallback(
    (event, val) => {
      setValue(val);
      setLoading(false);
      setOptions([]);
      setOpen(false);
    },
    [setValue, setLoading, setOptions, setOpen]
  );

  const onInputChange = useCallback(
    (event, newInputValue) => {
      // Only fire for typing input events. Element selection is a different event.
      if (event?.currentTarget?.nodeName === 'INPUT') {
        debouncedSource(newInputValue);
      }
    },
    [debouncedSource]
  );

  const {field, popper, input} = useStyles({
    width,
    previewLabel,
    resultSize: commonOptions.length,
    total: totalResults,
  });

  return (
    <Autocomplete
      className={cn(className, field)}
      classes={sourceType === Source.COMMON ? {popper} : {}}
      multiple={false}
      open={open}
      onOpen={() => {
        setOpen(true);
        setSourceType(Source.COMMON);
      }}
      onClose={() => {
        setOpen(false);
        setOptions([]);
        setLoading(false);
      }}
      filterOptions={x => x}
      filterSelectedOptions
      autoComplete
      getOptionSelected={(option, value) => isEqual(option, value)}
      getOptionLabel={getLabel}
      onChange={onChange}
      value={value}
      onInputChange={onInputChange}
      options={sourceType === Source.ASYNC ? options : commonOptions}
      getOptionDisabled={isOptionDisabled}
      groupBy={getGroup}
      loading={loading}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          label=""
          variant="outlined"
          error={error}
          InputProps={{
            ...params.InputProps,
            className: input,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

const defaultPreviewLabel = (_resultSize, _total) =>
  'Choose from popular values below, or type to search';
const useStyles = makeStyles<any, any>(() => ({
  popper: ({previewLabel = defaultPreviewLabel, resultSize, total}) => ({
    width: '100%',
    '& > div::before': {
      display: 'inline-block',
      backgroundColor: '#CED5DE60',
      borderBottom: '1px solid #CED5DE',
      padding: '10px',
      content: resultSize !== total ? `"${previewLabel(resultSize, total)}"` : undefined,
    },
  }),
  field: ({width}) => ({
    width: width,
    minWidth: width,
    maxWidth: width,
  }),
  input: {
    lineHeight: 1,
    paddingTop: 0,
    paddingBottom: 0,
  },
}));
